import { Suspense, useContext } from "react";
import { icons } from "../common/icons";
import Card, { useThreshold } from "./Card";
import { HTMLSectionWithLink } from "./elements/Link";
import Img from "./elements/Img";
import Prices from "./elements/Prices";
import CurrencyContext from "../contexts/CurrencyContext";
import { Offer, Settings } from "../domain";
import RegtriggeringAnimatedCheckmark from "./elements/RegtriggeringAnimatedCheckmark";
import ProductsContext from "../contexts/ProductsContext";
import { getAvailableOfferTargetVariantsSet } from "../core/products";

function getSubtitle(offer: Offer, settings, passed: boolean | undefined) {
  const subtitle = passed
    ? settings.subtitle_condition_met || "Enjoy your free gifts"
    : offer.subtitle_condition_not_met || "to get a free DISPLAYNAME";

  return (
    <HTMLSectionWithLink>
      {subtitle.replace("DISPLAYNAME", offer.display_name)}
    </HTMLSectionWithLink>
  );
}

export const ServiceCardInner = ({ offer }) => {
  const Icon = icons?.[offer.icon]?.icon ?? null;
  const { passed } = useThreshold(offer);
  const currencyCtx = useContext(CurrencyContext);
  const giftQuantity = offer.gift_quantity;
  const giftQuantityNode =
    giftQuantity && giftQuantity > 1 ? `${giftQuantity}× ` : '';
  return (
    <div data-cy="inner-service-card" className="Giftbox-InnerServiceCard">
      <div className="flex gap-4 items-center">
        <Img>
          <Suspense fallback={<span></span>}>
            <Icon />
          </Suspense>

          {passed ? <RegtriggeringAnimatedCheckmark /> : null}
        </Img>
        <div className="GiftBox-Offer-Card-Line flex flex-col justify-center Giftbox-CardProductNameWidth">
          <div className="GiftBox-Offer-Card-Line-Name GiftBox-Offer-Service-Name Giftbox-TextOverflowEllipsis">
            {giftQuantityNode}
            {offer.display_name}
          </div>
          <Prices originalPrice={offer.original_price} currency={currencyCtx} />
        </div>
      </div>
    </div>
  );
};

type Props = {
  offer: Offer;
  settings: Settings;
};

export default ({ offer, settings }: Props) => {
  const { passed } = useThreshold(offer);
  const productsCtx = useContext(ProductsContext);
  const targetProduct = offer.target_shopify_product_handle
    ? productsCtx.getTargetProduct(offer.target_shopify_product_handle)
    : undefined;
  const availableOfferTargetVariantsSet = getAvailableOfferTargetVariantsSet(
    offer,
    targetProduct
  );

  if (offer.target_shopify_product_handle && availableOfferTargetVariantsSet.size === 0) {
    return null;
  }

  return (
    <Suspense fallback={<p>Loading...</p>}>
      <Card
        offer={offer}
        subtitle={getSubtitle(offer, settings, passed)}
        isMultipleProduct={false}
      >
        <ServiceCardInner offer={offer} />
      </Card>
    </Suspense>
  );
};
